import { Box, Stack, Typography } from '@mui/material'
import { Check as CheckIcon, Circle as CircleIcon } from '@mui/icons-material'

import { PulsingDot } from '@/apps/latest/components/atoms/PulsingDot'
import { ICON_SIZE } from '@/shared/styles/typography'

interface Step {
  readonly id: string
  readonly label: string
}

// 5-step 正典. 初回 SOAP 生成 (= MeetingBlock 録音→完了) + 再生成 (= PostRecordingView)
// 両 caller が参照する. step 3 は 「評価と方針を組み立て」 = LLM SOAP 生成本体.
export const PROCESSING_STEPS = [
  { id: 'upload', label: '音声をアップロード' },
  { id: 'denoise', label: '会話のノイズを除去' },
  { id: 'entity', label: '医療用語を抽出' },
  { id: 'assess', label: '評価と方針を組み立て' },
  { id: 'completed', label: '生成完了' },
] as const

interface Props {
  steps: ReadonlyArray<Step>
  // 現在の進行 step index. idx < currentIdx は done、 idx === currentIdx は in-progress、
  // idx > currentIdx は pending. allDone=true なら全 step done として表示 (currentIdx は無視).
  // log/33 P5.41.9: 「completed」 step を steps 末尾に in-list 化し finalLabel を撤去.
  currentIdx: number
  allDone?: boolean
}

const ROW_MIN_HEIGHT = 26
// in-progress dot を CircleIcon と同じ視覚体力に揃えるサイズ.
const PROGRESS_DOT_SIZE = 14

type RowIcon =
  | { kind: 'check' }
  | { kind: 'circle'; color: string }
  | { kind: 'pulse' }

const Row = ({
  icon,
  textColor,
  label,
}: {
  icon: RowIcon
  textColor: string
  label: string
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      minHeight: ROW_MIN_HEIGHT,
    }}
  >
    {icon.kind === 'check' && (
      <CheckIcon sx={{ fontSize: ICON_SIZE.md, color: 'text.primary' }} />
    )}
    {icon.kind === 'circle' && (
      <CircleIcon sx={{ fontSize: ICON_SIZE.md, color: icon.color }} />
    )}
    {icon.kind === 'pulse' && <PulsingDot size={PROGRESS_DOT_SIZE} />}
    <Typography variant="body2" sx={{ color: textColor }}>
      {label}
    </Typography>
  </Box>
)

export const ProcessingChecklist = ({ steps, currentIdx, allDone }: Props) => (
  <Stack spacing={0.75} sx={{ py: 0.5 }}>
    {steps.map((step, idx) => {
      const isDone = allDone || idx < currentIdx
      const isInProgress = !allDone && idx === currentIdx
      if (isDone) {
        return (
          <Row
            key={step.id}
            icon={{ kind: 'check' }}
            textColor="text.primary"
            label={step.label}
          />
        )
      }
      if (isInProgress) {
        return (
          <Row
            key={step.id}
            icon={{ kind: 'pulse' }}
            textColor="text.primary"
            label={`${step.label}中…`}
          />
        )
      }
      return (
        <Row
          key={step.id}
          icon={{ kind: 'circle', color: 'text.disabled' }}
          textColor="text.disabled"
          label={step.label}
        />
      )
    })}
  </Stack>
)
